import { useEffect, useRef, useState } from "react";
import type { FrontDeskCounts } from "../pages/bookings/frontDeskTypes";

/**
 * Top-bar bell: what needs a staff decision for the selected business.
 * Polls while the tab is visible; switching business clears the count and
 * drops answers that arrive for the previous business. The fetcher is
 * injected so the hook is testable without the axios layer.
 */
export function useFrontDeskCounts(
  businessId: string | undefined,
  fetchCounts: (businessId: string) => Promise<FrontDeskCounts>,
  intervalMs = 60_000,
): { total: number; counts: FrontDeskCounts | null } {
  const [counts, setCounts] = useState<FrontDeskCounts | null>(null);
  const currentBiz = useRef(businessId);
  const fetchRef = useRef(fetchCounts);
  fetchRef.current = fetchCounts;

  useEffect(() => {
    currentBiz.current = businessId;
    setCounts(null);
    if (!businessId) return;

    const load = () => {
      if (document.visibilityState !== "visible") return;
      fetchRef
        .current(businessId)
        .then((c) => {
          if (currentBiz.current === businessId) setCounts(c);
        })
        .catch(() => {
          // Keep the last count; the next tick retries.
        });
    };
    load();
    const timer = setInterval(load, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [businessId, intervalMs]);

  const total = counts
    ? counts.overduePayments + counts.callList + counts.changeRequests + counts.refundsOwed
    : 0;
  return { total, counts };
}
